import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { leavesApi, sickLeavesApi } from "../api/leaves";

export function useLeaveRequests(params?: Record<string, string>) {
  return useQuery({
    queryKey: ["leave-requests", params],
    queryFn: () => leavesApi.list(params),
    staleTime: 0,
  });
}

export function usePendingLeavesCount(enabled = true) {
  return useQuery({
    queryKey: ["leave-requests", { status: "pending,cancellation_pending" }],
    queryFn: () => leavesApi.list({ status: "pending,cancellation_pending" }),
    enabled,
    select: (items) => items.length,
  });
}

export function useCreateLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) => leavesApi.create(formData),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leave-requests"] }),
  });
}

/** Employees this reviewer may file a leave request on behalf of. */
export function useLeaveProxyTargets(enabled = true) {
  return useQuery({
    queryKey: ["leave-proxy-targets"],
    queryFn: () => leavesApi.proxyTargets(),
    enabled,
    staleTime: 1000 * 60 * 5,
  });
}

export function useApproveLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      leavesApi.approve(id, comment),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leave-requests"] }),
  });
}

export function useRejectLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      leavesApi.reject(id, comment),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leave-requests"] }),
  });
}

export function useWithdrawLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => leavesApi.withdraw(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leave-requests"] }),
  });
}

export function useRequestCancellationLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      leavesApi.requestCancellation(id, comment),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leave-requests"] }),
  });
}

export function useApproveCancellationLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => leavesApi.approveCancellation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leave-requests"] }),
  });
}

// ── Sick Leave (L4) hooks ─────────────────────────────────────────────────────

export function useSickLeaves(params?: Record<string, string>) {
  return useQuery({
    queryKey: ["sick-leaves", params],
    queryFn: () => sickLeavesApi.list(params),
    staleTime: 0,
  });
}

export function usePendingSickLeavesCount(enabled = true) {
  return useQuery({
    queryKey: ["sick-leaves", { status: "pending" }],
    queryFn: () => sickLeavesApi.list({ status: "pending" }),
    enabled,
    select: (r) => r.meta.count,
  });
}

/** Employees this L4 verifier may file a sick leave on behalf of. */
export function useSickLeaveProxyTargets(enabled = true) {
  return useQuery({
    queryKey: ["sick-leave-proxy-targets"],
    queryFn: () => sickLeavesApi.proxyTargets(),
    enabled,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateSickLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { employee?: string; start_date: string; end_date: string; note?: string; verify_now?: boolean; verification_comment?: string }) =>
      sickLeavesApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sick-leaves"] }),
  });
}

export function useVerifySickLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      sickLeavesApi.verify(id, comment),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sick-leaves"] }),
  });
}

export function useRejectSickLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      sickLeavesApi.reject(id, comment),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sick-leaves"] }),
  });
}

export function useDeleteSickLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sickLeavesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sick-leaves"] }),
  });
}
